import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, Infinity as InfinityIcon, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { convertFromUSD, formatMoney, getFxRates, subscribeFxRates } from "@/lib/currency";

/** Logged-in tenant upgrade / renewal.
 *
 *   /upgrade                  -> Solo lifetime (₹1500 one-time)
 *   /upgrade?plan=studio      -> Studio monthly/annual renewal
 *
 * Uses the authenticated /payments/tenant/create-order + /tenant/verify pair.
 */
export default function Upgrade() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user, tenant, reload } = useAuth();
  const targetPlan = sp.get("plan") === "studio" ? "studio" : "solo";

  const [cycle, setCycle] = useState(targetPlan === "studio" ? "monthly" : "lifetime");
  const [busy, setBusy] = useState(false);
  const [fx, setFx] = useState(getFxRates());

  useEffect(() => subscribeFxRates(setFx), []);

  const currency = tenant?.currency || "INR";
  const inrAmount = useMemo(() => {
    if (targetPlan === "solo") return 1500;
    if (cycle === "annual") return 11389;
    return 999;
  }, [targetPlan, cycle]);

  const displayPrice = useMemo(() => {
    if (currency === "INR") return formatMoney(inrAmount, "INR", { maximumFractionDigits: 0 });
    const rate = fx?.rates?.INR;
    if (!rate) return `₹${inrAmount}`;
    const usd = inrAmount / rate;
    return formatMoney(convertFromUSD(usd, currency), currency, { maximumFractionDigits: 0 });
  }, [inrAmount, currency, fx]);

  const pay = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    setBusy(true);
    try {
      const { data: order } = await api.post("/payments/tenant/create-order", {
        plan: targetPlan,
        billing_cycle: cycle,
      });
      if (!window.Razorpay) {
        toast.error("Razorpay could not load. Refresh and try again.");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Kairosa",
        description:
          targetPlan === "solo"
            ? "Solo Lifetime · One-time payment"
            : `Studio · ${cycle === "annual" ? "Annual (5% off)" : "Monthly"}`,
        image: "/favicon.ico",
        prefill: { email: user.email, name: user.name },
        theme: { color: "#e97035" },
        handler: async (resp) => {
          try {
            await api.post("/payments/tenant/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful. Welcome to " + (targetPlan === "solo" ? "Solo Lifetime" : "Studio") + ".");
            await reload?.();
            nav("/app");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed.");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <Link
          to="/app"
          data-testid="upgrade-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to workspace
        </Link>

        <div className="mt-8 flex items-center gap-3">
          <InfinityIcon className="h-8 w-8 text-primary" />
          <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            Upgrade · {targetPlan === "solo" ? "Solo Lifetime" : "Studio"}
          </div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mt-3">
          {targetPlan === "solo" ? "One payment. Yours for life." : "Keep the momentum going."}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          {targetPlan === "solo"
            ? "Pay ₹1,500 once (~$18) and keep your Solo workspace forever. No renewals, no surprises."
            : "Renew your Studio plan to keep Core Platform, 3 modules and AI Assistant active without interruption."}
        </p>

        {targetPlan === "studio" && (
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="upgrade-cycle-monthly"
              onClick={() => setCycle("monthly")}
              className={"text-left rounded-xl border p-5 transition " + (cycle === "monthly" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40")}
            >
              <div className="font-display text-xl">Monthly</div>
              <div className="text-xs text-muted-foreground mt-1">Billed every 30 days</div>
              <div className="font-display text-2xl mt-3">₹999</div>
            </button>
            <button
              type="button"
              data-testid="upgrade-cycle-annual"
              onClick={() => setCycle("annual")}
              className={"text-left rounded-xl border p-5 transition relative " + (cycle === "annual" ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40")}
            >
              <span className="absolute -top-2 left-4 text-[9px] uppercase tracking-[0.22em] bg-accent text-accent-foreground px-2 py-0.5 rounded-full">Save 5%</span>
              <div className="font-display text-xl">Annual</div>
              <div className="text-xs text-muted-foreground mt-1">Billed once per year · 5% discount</div>
              <div className="font-display text-2xl mt-3">₹11,389</div>
            </button>
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-baseline justify-between">
            <div className="font-display text-lg">
              {targetPlan === "solo" ? "Solo Lifetime" : `Studio · ${cycle === "annual" ? "Annual" : "Monthly"}`}
            </div>
            <div className="font-display text-3xl">{displayPrice}</div>
          </div>
          {currency !== "INR" && (
            <div className="text-[11px] text-muted-foreground mt-1">Charged as ₹{inrAmount.toLocaleString("en-IN")} INR · live-converted</div>
          )}
          <div className="mt-6 space-y-2 text-sm text-muted-foreground">
            {(targetPlan === "solo"
              ? ["Lifetime access — never pay again", "Core Platform + 1 module", "1 team seat", "Community support"]
              : ["Core Platform + 3 modules", "3 team seats", "AI Assistant included", "Cancel anytime"]
            ).map((f) => (
              <div key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {f}</div>
            ))}
          </div>

          <Button data-testid="upgrade-pay-btn" onClick={pay} disabled={busy} className="w-full h-12 mt-6 rounded-full">
            <CreditCard className="h-4 w-4 mr-2" />
            {busy ? "Opening secure checkout…" : `Pay ${displayPrice}`}
          </Button>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" />
            Secured by Razorpay · Cards · UPI · Netbanking · Wallets
          </div>
        </div>
      </div>
    </div>
  );
}
